import { lazy } from 'react';
import withSuspense from '@components/withSuspense';
import { AlertColor } from '@mui/material';
import { ConsingneeModalProps } from '@/Consignee/types';
import { BulkMakeContainerAvailableProps, MakeContainerAvailableProps } from '@/MakeContainerAvl/types';
import { ViewDispatchEventsProps } from '@/ViewDispatchEvents/types';
import { EmailDocumentsProps, AddDocumentProps, EditDocumentProps } from '@/ManageDocuments/types';
import { SendPudlReceiptProps } from '@/PickupDeliveryReceipt/types';
import { StreetTurnProps } from '@/StreetTurn/types';
import { RailBillingProps } from '@/RailBilling/types';
import { ConfirmPopupProps } from '@components/Modals/ConfirmPopup';
import { DocumentControlModalProps } from '@/DocumentControl/types';
import MakeContainerAvlModal from '@/MakeContainerAvl/MakeContainerAvailable';
import SendPuDlReceiptModal from '@/PickupDeliveryReceipt/SendPudlReceipt';
import ManagePuDlModal from '@/ManagePickupDelivery/Modal';
import DispatchEventsModal from '@/ViewDispatchEvents/Modal';
import RailBillingModal from '@/RailBilling/RailBilling';
import StreetTurnModal from '@/StreetTurn/StreetTurn';
import ItemLocationHistoryModal from '@/ViewItemLocationHistory/Modal';
import ConsigneeModal from '@/Consignee/ConsigneeFormikModal';
import ReportModal from '@/Reports/ReportFormikModal';
import ConfirmPopup from '@/Components/Modals/ConfirmPopup';
import UpdateScheduledPuDlModal from '@/UpdateScheduledPickupDelivery/Modal';
import DocumentControlModal from '@/DocumentControl/DocumentControlModal';
import MultiContainerUpdateModal from '@/MultiContainerUpdate/MultiContainerUpdate';
import { ChargeFormikModalProps, PaymentFormikModalProps, RequestCreditModalProps } from '@components/ChargesAndPayments/types';
import ModalSuspenseFallback from './ModalSuspenseFallback';
import { ViewItemLocationHistoryProps } from '@/ViewItemLocationHistory/types';
import { ContactFormikModalProps } from '@/Contacts/types';
import { MultiContainerUpdateProps } from '@/MultiContainerUpdate/types';
import { AddWorkOrderContactModalProps } from '@/ConsignmentDetails/OrderTab/types';
import { ReminderFormikModalProps } from '@/Reminders/types';
import { VesselFormikModalProps, VesselVoyageFormikModalProps } from '@/Vessel/types';
import VesselFormikModal from '@/Vessel/VesselFormikModal';
import VesselVoyageFormikModal from '@/Vessel/VesselVoyageFormikModal';
import { RejectLoadTenderPromptProps } from '@/EdiLoadTender/RejectLoadTenderModal';
import { AddConsignmentFormikModalProps } from '@/AddConsignment/AddConsignmentFormikModal';
import { CancelReasonPromptProps } from '@/ConsignmentDetails/CancelReasonPrompt';
import { CopyQueryToSiteProps } from '@/BoardQueries/CopyQueryToSiteModal';
import { ViewDriverRouteProps } from '@/DriverRoute/types';

const LazyEmailDocumentsModal = withSuspense(
  lazy(() => import('@/ManageDocuments/EmailDocumentsModal')),
  ModalSuspenseFallback
);

const LazyAddDocumentModal = withSuspense(
  lazy(() => import('@/ManageDocuments/AddDocumentModal')),
  ModalSuspenseFallback
);

const LazyEditDocumentModal = withSuspense(
  lazy(() => import('@/ManageDocuments/EditDocumentModal')),
  ModalSuspenseFallback
);

const LazyChargeFormModal = withSuspense(
  lazy(() => import('@components/ChargesAndPayments/ChargeFormikModal')),
  ModalSuspenseFallback
);

const LazyPaymentFormModal = withSuspense(
  lazy(() => import('@components/ChargesAndPayments/PaymentFormikModal')),
  ModalSuspenseFallback
);

const LazyConsignmentFormModal = withSuspense(
  lazy(() => import('@/ConsignmentDetails/ConsignmentFormikModal')),
  ModalSuspenseFallback
);

const LazyContactFormModal = withSuspense(
  lazy(() => import('@/Contacts/ContactFormikModal')),
  ModalSuspenseFallback
);

const LazyRequestCreditModal = withSuspense(
  lazy(() => import('@components/ChargesAndPayments/RequestCreditModal')),
  ModalSuspenseFallback
);

const LazyAddWorkOrderContact = withSuspense(
  lazy(() => import('@/ConsignmentDetails/OrderTab/AddWorkOrderContact')),
  ModalSuspenseFallback
);

const LazyAddConsignmentModal = withSuspense(
  lazy(() => import('@/AddConsignment/AddConsignmentFormikModal')),
  ModalSuspenseFallback
);

const LazyReminderModal = withSuspense(
  lazy(() => import('@/Reminders/ReminderFormikModal')),
  ModalSuspenseFallback
);

const LazyEdiLoadTenderModal = withSuspense(
  lazy(() => import('@/EdiLoadTender/ViewLoadTenderModal')),
  ModalSuspenseFallback
);

const LazyEdiRejectLoadTenderModal = withSuspense(
  lazy(() => import('@/EdiLoadTender/RejectLoadTenderModal')),
  ModalSuspenseFallback
);

const LazyCopyPromptModal = withSuspense(lazy(() => import('@/ConsignmentDetails/CopyOrderPrompt')));

const LazyCancelReasonPromptModal = withSuspense(lazy(() => import('@/ConsignmentDetails/CancelReasonPrompt')));

const LazyTerminalPreferencesModal = withSuspense(
  lazy(() => import('@/TerminalPreferences/TerminalPreferencesFormikModal')),
  ModalSuspenseFallback
);

const LazyCopyQueryToSiteModal = withSuspense(
  lazy(() => import('@/BoardQueries/CopyQueryToSiteModal')),
  ModalSuspenseFallback
);

// Lazy so the Azure Maps control only downloads when a route is viewed
const LazyDriverRouteModal = withSuspense(
  lazy(() => import('@/DriverRoute/DriverRouteModal')),
  ModalSuspenseFallback
);

/**
 *
 */
export type ModalContextValues = null | {
  /** Collection of Announcements to display in the order recieved one at a time */
  modalAnnouncements: ModalAnnouncementProps[];
  /** Method to add an Announcement to the queue */
  queueModalAnnouncement: (announcement: Omit<ModalAnnouncementProps, 'id'>) => void;
  /** Method for removing an Announcement from the queue */
  removeModalAnnouncement: (id: number) => void;
  /** Reset the modalContext to initial values */
  reset: () => void;
};

/**
 *
 */
export interface ModalAnnouncementProps {
  /** Unique identifier generated for the announcement */
  id: number;
  /** MUI AlertColor dictating announcement style */
  severity: AlertColor;
  /** Text to be displayed in the announcement */
  message: string;
}

/**
 *	Mapping of the component string from RenderModalProps to the Component implementation
 */
export const modalComponents = {
  addDocumentModal: LazyAddDocumentModal,
  editDocumentModal: LazyEditDocumentModal,
  emailDocumentsModal: LazyEmailDocumentsModal,
  makeAvailableModal: MakeContainerAvlModal,
  bulkMakeAvailableModal: MakeContainerAvlModal,
  managePudlModal: ManagePuDlModal,
  updateScheduledPudlModal: UpdateScheduledPuDlModal,
  sendPudlReceiptModal: SendPuDlReceiptModal,
  streetTurnModal: StreetTurnModal,
  viewDispatchEventsModal: DispatchEventsModal,
  viewItemLocationHistoryModal: ItemLocationHistoryModal,
  consigneeModal: ConsigneeModal,
  reportModal: ReportModal,
  addVessel: VesselFormikModal,
  vesselVoyage: VesselVoyageFormikModal,
  documentControlModal: DocumentControlModal,
  confirmPopup: ConfirmPopup,
  railBillingModal: RailBillingModal,
  multiContainerUpdate: MultiContainerUpdateModal,
  consignmentDetails: LazyConsignmentFormModal,
  chargeForm: LazyChargeFormModal,
  paymentForm: LazyPaymentFormModal,
  contactForm: LazyContactFormModal,
  requestCredit: LazyRequestCreditModal,
  addWorkOrderContact: LazyAddWorkOrderContact,
  addConsignment: LazyAddConsignmentModal,
  reminderModal: LazyReminderModal,
  ediLoadTenderModal: LazyEdiLoadTenderModal,
  ediRejectLoadTenderModal: LazyEdiRejectLoadTenderModal,
  copyOrderPrompt: LazyCopyPromptModal,
  cancelReasonPrompt: LazyCancelReasonPromptModal,
  terminalPreferences: LazyTerminalPreferencesModal,
  copyQueryToSite: LazyCopyQueryToSiteModal,
  viewDriverRouteModal: LazyDriverRouteModal
};

export type ModalComponent =
  | AddDocumentModalComponent
  | EditDocumentModalComponent
  | EmailDocumentsModalComponent
  | MakeAvailableModalComponent
  | BulkMakeAvailableModalComponent
  | SendPudlReceiptComponent
  | StreetTurnComponent
  | ViewDispatchEventsComponent
  | ViewItemLocationHistoryComponent
  | ManagePudlComponent
  | UpdateScheduledPudlComponent
  | ConsigneeComponent
  | ReportComponent
  | DocumentControlComponent
  | ConfirmComponent
  | ConsignmentDetailsComponent
  | ChargeFormComponent
  | PaymentFormComponent
  | RailBillingComponent
  | ContactFormComponent
  | RequestCreditComponent
  | MultiContainerUpdateComponent
  | AddWorkOrderContactComponent
  | AddConsignmentComponent
  | ReminderComponent
  | EdiLoadTenderComponent
  | EdiRejectLoadTenderComponent
  | AddVesselComponent
  | VesselVoyageComponent
  | CopyOrderPromptComponent
  | CancelReasonPromptComponent
  | TerminalPreferencesComponent
  | CopyQueryToSiteComponent
  | ViewDriverRouteComponent;

/**
 *	Available Modal types to the Application that configures the rendering component and props for typescript
 */
export type RenderModalProps = ModalComponent & {
  /** Unique identifier for the queue */
  id: number;
  /** By default, the modal will be added to the stack. If true, the stack will be replaced with the new modal. */
  replaceStack?: boolean;
  /** By default, Props are unknown until specified by the ModalComponent */
  Props?: unknown;
};

interface AddDocumentModalComponent {
  Component: 'addDocumentModal';
  Props: AddDocumentProps;
}

interface EditDocumentModalComponent {
  Component: 'editDocumentModal';
  Props: EditDocumentProps;
}

interface EmailDocumentsModalComponent {
  Component: 'emailDocumentsModal';
  Props: EmailDocumentsProps;
}

interface MakeAvailableModalComponent {
  Component: 'makeAvailableModal';
  Props: MakeContainerAvailableProps;
}

interface BulkMakeAvailableModalComponent {
  Component: 'bulkMakeAvailableModal';
  Props: BulkMakeContainerAvailableProps;
}

interface SendPudlReceiptComponent {
  Component: 'sendPudlReceiptModal';
  Props: SendPudlReceiptProps;
}

interface StreetTurnComponent {
  Component: 'streetTurnModal';
  Props: StreetTurnProps;
}

interface ViewDispatchEventsComponent {
  Component: 'viewDispatchEventsModal';
  Props: ViewDispatchEventsProps;
}

interface ViewItemLocationHistoryComponent {
  Component: 'viewItemLocationHistoryModal';
  Props: ViewItemLocationHistoryProps;
}

interface ManagePudlComponent {
  Component: 'managePudlModal';
  Props: { legId: number };
}

interface UpdateScheduledPudlComponent {
  Component: 'updateScheduledPudlModal';
  Props: { legIds: number[] };
}

interface ConsigneeComponent {
  Component: 'consigneeModal';
  Props: ConsingneeModalProps;
}

interface ReportComponent {
  Component: 'reportModal';
}

interface DocumentControlComponent {
  Component: 'documentControlModal';
  Props: DocumentControlModalProps;
}

interface ConfirmComponent {
  Component: 'confirmPopup';
  Props: ConfirmPopupProps;
}

interface ConsignmentDetailsComponent {
  Component: 'consignmentDetails';
}

interface ChargeFormComponent {
  Component: 'chargeForm';
  Props: ChargeFormikModalProps;
}

interface PaymentFormComponent {
  Component: 'paymentForm';
  Props: PaymentFormikModalProps;
}

interface RailBillingComponent {
  Component: 'railBillingModal';
  Props: RailBillingProps;
}

interface ContactFormComponent {
  Component: 'contactForm';
  Props: ContactFormikModalProps;
}

interface RequestCreditComponent {
  Component: 'requestCredit';
  Props: RequestCreditModalProps;
}

interface MultiContainerUpdateComponent {
  Component: 'multiContainerUpdate';
  Props: MultiContainerUpdateProps;
}

interface AddWorkOrderContactComponent {
  Component: 'addWorkOrderContact';
  Props: AddWorkOrderContactModalProps;
}

interface AddConsignmentComponent {
  Component: 'addConsignment';
  Props: AddConsignmentFormikModalProps;
}

interface AddVesselComponent {
  Component: 'addVessel';
  Props: VesselFormikModalProps;
}

interface VesselVoyageComponent {
  Component: 'vesselVoyage';
  Props: VesselVoyageFormikModalProps;
}

interface ReminderComponent {
  Component: 'reminderModal';
  Props: ReminderFormikModalProps;
}

interface EdiLoadTenderComponent {
  Component: 'ediLoadTenderModal';
}

interface EdiRejectLoadTenderComponent {
  Component: 'ediRejectLoadTenderModal';
  Props: RejectLoadTenderPromptProps;
}

interface CopyOrderPromptComponent {
  Component: 'copyOrderPrompt';
  Props: AddConsignmentFormikModalProps;
}

interface CancelReasonPromptComponent {
  Component: 'cancelReasonPrompt';
  Props: CancelReasonPromptProps;
}

interface TerminalPreferencesComponent {
  Component: 'terminalPreferences';
}

interface CopyQueryToSiteComponent {
  Component: 'copyQueryToSite';
  Props: CopyQueryToSiteProps;
}

interface ViewDriverRouteComponent {
  Component: 'viewDriverRouteModal';
  Props: ViewDriverRouteProps;
}
